import React, { useState, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  CCard,
  CCardHeader,
  CCardBody,
  CButton,
  CSpinner,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilCloudDownload } from "@coreui/icons";
import CustomVideoPlayer from "../../../components/CustomVideoPlayer/CustomVideoPlayer";
import ThinkingOrb from "../../../components/ThinkingOrb/ThinkingOrb";
import i18n from "../../../../i18n";

const CameraVideoPlayer = ({
  t,
  selectedVideo,
  showDetections,
  setShowDetections,
  setExportModalVisible,
  isDetectionsLoading,
  customPlayerRef,
  seekToTime,
  seekTriggerId,
  canvasRef,
  onEnded,
  onProgress,
  variant,
  crossVideo,
  compact,
}) => {
  const isTruck = variant === "truck";

  const [syncViewEnabled, setSyncViewEnabled] = useState(true);
  const secondaryVideoRef = useRef(null);

  const [secondaryReady, setSecondaryReady] = useState(false);
  const manualNudgeSecState = useState(0);
  const [manualNudgeSec, setManualNudgeSec] = manualNudgeSecState;

  // Опорные точки времени от сервера для текущего клика
  const primaryAnchorRef = useRef(0);
  const secondaryAnchorRef = useRef(0);

  const hasCrossVideo = Boolean(crossVideo && crossVideo.video_url);
  const isDualMode = hasCrossVideo && syncViewEnabled;

  // 1. Сброс состояния при смене видеофайлов
  useEffect(() => {
    setSecondaryReady(false);
    setManualNudgeSec(0);
  }, [selectedVideo, crossVideo?.video_url, setManualNudgeSec]);

  // 2. Коэффициент соотношения длительностей таймлапсов
  const getTimelapseRatio = useCallback(() => {
    const primary = customPlayerRef.current?.getInternalPlayer();
    const secondary = secondaryVideoRef.current;
    if (primary && secondary && primary.duration > 0 && secondary.duration > 0) {
      return secondary.duration / primary.duration;
    }
    return 1.0;
  }, [customPlayerRef]);

  // 3. Запоминаем точные серверные координаты каждого клика
  useEffect(() => {
    if (crossVideo) {
      primaryAnchorRef.current = seekToTime || 0;
      secondaryAnchorRef.current = crossVideo.seek_seconds || 0;
    }
  }, [crossVideo, seekToTime, seekTriggerId]);

  // 4. Расчет положения второго видео с компенсацией разницы скоростей таймлапса
  const calculateSecondaryTargetTime = useCallback(
    (primaryCurrentTime) => {
      const pCur = primaryCurrentTime ?? 0;
      const pStart = primaryAnchorRef.current;
      const sStart = secondaryAnchorRef.current;

      const ratio = getTimelapseRatio();
      const elapsedPrimary = pCur - pStart;

      // Пройденное время масштабируется по разнице длительностей файлов
      const target = sStart + elapsedPrimary * ratio + manualNudgeSec;

      if (Number.isFinite(target) && target >= 0) {
        return target;
      }
      return sStart + manualNudgeSec;
    },
    [getTimelapseRatio, manualNudgeSec]
  );

  // 5. Перемотка второго видео при клике
  useEffect(() => {
    if (!isDualMode) return;
    const secondary = secondaryVideoRef.current;
    if (!secondary || secondary.readyState < 1) return;

    const targetTime = (crossVideo?.seek_seconds || 0) + manualNudgeSec;
    if (Number.isFinite(targetTime) && targetTime >= 0) {
      secondary.currentTime = targetTime;
    }
  }, [seekTriggerId, crossVideo?.seek_seconds, manualNudgeSec, isDualMode]);

  const handleSecondaryLoadedMetadata = () => {
    const secondary = secondaryVideoRef.current;
    if (!secondary) return;

    const target = (crossVideo?.seek_seconds || 0) + manualNudgeSec;
    if (Number.isFinite(target) && target >= 0) {
      secondary.currentTime = target;
    }
  };

  const handleSecondaryCanPlay = () => {
    setSecondaryReady(true);
  };

  // 6. НЕПРЕРЫВНЫЙ КОНТРОЛЛЕР СИНХРОНИЗАЦИИ (каждые 500 мс подгоняет видео и балансирует скорость)
  useEffect(() => {
    if (!isDualMode) return;

    const intervalId = setInterval(() => {
      const primary = customPlayerRef.current?.getInternalPlayer();
      const secondary = secondaryVideoRef.current;
      if (!primary || !secondary || secondary.readyState < 2) return;

      const targetSec = calculateSecondaryTargetTime(primary.currentTime);
      if (targetSec === null || !Number.isFinite(targetSec) || targetSec < 0) return;

      const diff = Math.abs(secondary.currentTime - targetSec);
      const ratio = getTimelapseRatio();

      if (primary.paused) {
        // НА ПАУЗЕ: принудительно держим в одной точке
        if (!secondary.paused) {
          secondary.pause();
        }
        if (diff > 0.15) {
          secondary.currentTime = targetSec;
        }
      } else {
        // ПРИ ВОСПРОИЗВЕДЕНИИ:
        if (secondary.paused && !isDetectionsLoading) {
          secondary.play().catch(() => {});
        }
        // Скорость второго видео адаптируется под его реальный FPS таймлапса
        const targetRate = primary.playbackRate * ratio;
        if (Math.abs(secondary.playbackRate - targetRate) > 0.05) {
          secondary.playbackRate = targetRate;
        }
        // Если из-за сетевых лагов разошлись больше чем на 0.3 сек — подтягиваем
        if (diff > 0.3) {
          secondary.currentTime = targetSec;
        }
      }
    }, 500);

    return () => clearInterval(intervalId);
  }, [isDualMode, isDetectionsLoading, calculateSecondaryTargetTime, getTimelapseRatio, customPlayerRef]);

  // Синхронизация стандартных событий Play/Pause/Rate
  useEffect(() => {
    if (!isDualMode) return;
    const primary = customPlayerRef.current?.getInternalPlayer();
    const secondary = secondaryVideoRef.current;
    if (!primary || !secondary) return;

    const handlePlay = () => {
      if (!secondaryReady || isDetectionsLoading) {
        primary.pause();
        return;
      }
      const targetSec = calculateSecondaryTargetTime(primary.currentTime);
      if (Number.isFinite(targetSec) && targetSec >= 0) {
        secondary.currentTime = targetSec;
      }
      secondary.playbackRate = primary.playbackRate * getTimelapseRatio();
      secondary.play().catch(() => {});
    };

    const handlePause = () => {
      secondary.pause();
    };

    const handleRateChange = () => {
      secondary.playbackRate = primary.playbackRate * getTimelapseRatio();
    };

    primary.addEventListener("play", handlePlay);
    primary.addEventListener("pause", handlePause);
    primary.addEventListener("ratechange", handleRateChange);

    return () => {
      primary.removeEventListener("play", handlePlay);
      primary.removeEventListener("pause", handlePause);
      primary.removeEventListener("ratechange", handleRateChange);
    };
  }, [isDualMode, secondaryReady, isDetectionsLoading, calculateSecondaryTargetTime, getTimelapseRatio, customPlayerRef]);

  const isWaitingSync = isDualMode && (!secondaryReady || isDetectionsLoading);

  return (
    <CCard className="h-100 d-flex flex-column border-0 shadow-sm">
      {/* compact - тонкая шапка для окна самосвала на невысоком экране */}
      <CCardHeader
        className={`d-flex justify-content-between align-items-center flex-wrap gap-2 flex-shrink-0 ${
          compact ? "py-1 px-2" : "py-2"
        }`}
      >
        <span className={`fw-bold${compact ? " small" : ""}`}>{t("camera.view_video")}</span>
        <div className="d-flex align-items-center flex-wrap gap-3">
          {hasCrossVideo && (
            <div className="form-check form-switch d-flex align-items-center mb-0">
              <input
                className="form-check-input m-0 shadow-none"
                type="checkbox"
                role="switch"
                id="syncViewSwitch"
                checked={syncViewEnabled}
                onChange={(e) => setSyncViewEnabled(e.target.checked)}
                style={{ cursor: "pointer", width: "40px", height: "20px" }}
              />
              <label
                className="form-check-label ms-2 mb-0 fw-bold text-primary"
                htmlFor="syncViewSwitch"
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                🎥 {i18n.t('camera_video_player.sync_view', 'Синхронный ракурс')}
              </label>
            </div>
          )}

          {!isTruck && (
            <div className="form-check form-switch d-flex align-items-center mb-0">
              <input
                className="form-check-input m-0 shadow-none"
                type="checkbox"
                role="switch"
                id="showDetectionsSwitch"
                checked={showDetections}
                onChange={(e) => setShowDetections(e.target.checked)}
                style={{ cursor: "pointer", width: "40px", height: "20px" }}
              />
              <label
                className="form-check-label ms-2 mb-0 fw-semibold"
                htmlFor="showDetectionsSwitch"
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                {t("camera_modals.show_ai_boxes")}
              </label>
            </div>
          )}
          <CButton
            size="sm"
            color="primary"
            variant="outline"
            onClick={() => setExportModalVisible(true)}
          >
            <CIcon icon={cilCloudDownload} className="me-1" /> i18n.t('camera_video_player.merge_and_export', 'Склейка и выгрузка')
          </CButton>
        </div>
      </CCardHeader>

      <CCardBody
        className={`flex-grow-1 d-flex flex-column overflow-hidden ${compact ? "p-1" : "p-1 p-md-2"}`}
        style={{ minHeight: 0, backgroundColor: "#000" }}
      >
        {selectedVideo ? (
          <div
            className="video-wrapper p-0 position-relative flex-grow-1 w-100 d-flex flex-column flex-md-row gap-2"
            style={{
              minHeight: 0,
              backgroundColor: "#000",
              overflow: "hidden",
            }}
          >
            {/* 1. ГЛАВНОЕ ВЕДУЩЕЕ ВИДЕО */}
            <div
              className="position-relative h-100"
              style={{
                flex: isDualMode ? "1 1 50%" : "1 1 100%",
                width: isDualMode ? "50%" : "100%",
                minHeight: 0,
                backgroundColor: "#000",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              {(isDetectionsLoading || isWaitingSync) && (
                <div
                  className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center"
                  style={{
                    zIndex: 25,
                    backgroundColor: "rgba(0,0,0,0.72)",
                    backdropFilter: "blur(4px)",
                    pointerEvents: "none",
                  }}
                >
                  <ThinkingOrb
                    size="md"
                    text={
                      isWaitingSync
                        ? "i18n.t('camera_video_player.syncing_views', 'Синхронизация ракурсов...')"
                        : "i18n.t('camera_video_player.ai_analyzing', 'ИИ анализирует кадры...')"
                    }
                  />
                </div>
              )}

              <div
                className="video-container"
                style={{
                  width: "100%",
                  height: "100%",
                  position: "relative",
                  backgroundColor: "#000",
                }}
              >
                <CustomVideoPlayer
                  key={selectedVideo}
                  ref={customPlayerRef}
                  url={selectedVideo}
                  initialSeekTime={seekToTime}
                  seekTriggerId={seekTriggerId}
                  isLoading={isDetectionsLoading || isWaitingSync}
                  onReady={() => {}}
                  onEnded={onEnded}
                  onProgress={onProgress}
                  width="100%"
                  height="100%"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundColor: "#000",
                  }}
                >
                  {!isTruck && (
                    <canvas
                      ref={canvasRef}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        pointerEvents: "none",
                        zIndex: 10,
                      }}
                    />
                  )}
                </CustomVideoPlayer>
              </div>
            </div>

            {/* 2. ВТОРОЕ ВЕДОМОЕ ВИДЕО */}
            {isDualMode && (
              <div
                className="position-relative h-100 overflow-hidden"
                style={{
                  flex: "1 1 50%",
                  width: "50%",
                  minHeight: 0,
                  backgroundColor: "#000",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {/* Плашка с именем машины и кнопками тонкой ручной подстройки */}
                <div
                  className="position-absolute top-0 start-0 m-2 px-2 py-1 rounded small fw-bold text-white shadow d-flex align-items-center gap-2"
                  style={{
                    backgroundColor: "rgba(0, 0, 0, 0.75)",
                    backdropFilter: "blur(4px)",
                    border: "1px solid rgba(255, 255, 255, 0.25)",
                    zIndex: 15,
                    fontSize: "12px",
                  }}
                >
                  <span>🎥 Камера: {crossVideo.device_name || "Синхронный ракурс"}</span>

                  <div className="d-flex align-items-center gap-1 ms-2 border-start ps-2">
                    <button
                      type="button"
                      onClick={() => setManualNudgeSec((n) => n - 0.5)}
                      className="btn btn-sm btn-dark py-0 px-1 text-white border-secondary"
                      style={{ fontSize: "10px", lineHeight: "14px" }}
                      title="i18n.t('camera_video_player.shift_back', 'Сдвинуть назад на 0.5с')"
                    >
                      -0.5с
                    </button>
                    <button
                      type="button"
                      onClick={() => setManualNudgeSec((n) => n + 0.5)}
                      className="btn btn-sm btn-dark py-0 px-1 text-white border-secondary"
                      style={{ fontSize: "10px", lineHeight: "14px" }}
                      title="i18n.t('camera_video_player.shift_forward', 'Сдвинуть вперед на 0.5с')"
                    >
                      +0.5с
                    </button>
                    {manualNudgeSec !== 0 && (
                      <button
                        type="button"
                        onClick={() => setManualNudgeSec(0)}
                        className="btn btn-sm btn-outline-danger py-0 px-1"
                        style={{ fontSize: "10px", lineHeight: "14px" }}
                        title="i18n.t('camera_video_player.reset_manual_shift', 'Сбросить ручной сдвиг')"
                      >
                        {manualNudgeSec > 0 ? `+${manualNudgeSec}` : manualNudgeSec}с ✕
                      </button>
                    )}
                  </div>
                </div>

                {!secondaryReady && (
                  <div
                    className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center"
                    style={{
                      zIndex: 12,
                      backgroundColor: "#000",
                    }}
                  >
                    <CSpinner color="light" size="sm" className="mb-2" />
                    <span className="text-white small">i18n.t('camera_video_player.connecting_camera', 'Подключение камеры...')</span>
                  </div>
                )}

                <video
                  ref={secondaryVideoRef}
                  src={crossVideo.video_url}
                  playsInline
                  muted
                  preload="auto"
                  onLoadedMetadata={handleSecondaryLoadedMetadata}
                  onCanPlay={handleSecondaryCanPlay}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    backgroundColor: "#000",
                    pointerEvents: "none",
                  }}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="text-center p-4 text-muted flex-grow-1 d-flex align-items-center justify-content-center">
            {t("camera.select_recording")}
          </div>
        )}
      </CCardBody>
    </CCard>
  );
};

CameraVideoPlayer.propTypes = {
  t: PropTypes.func.isRequired,
  selectedVideo: PropTypes.string,
  showDetections: PropTypes.bool,
  setShowDetections: PropTypes.func,
  setExportModalVisible: PropTypes.func,
  isDetectionsLoading: PropTypes.bool,
  customPlayerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  seekToTime: PropTypes.number,
  seekTriggerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  canvasRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  onEnded: PropTypes.func,
  onProgress: PropTypes.func,
  variant: PropTypes.oneOf(["excavator", "truck"]),
  crossVideo: PropTypes.shape({
    video_url: PropTypes.string,
    seek_seconds: PropTypes.number,
    device_name: PropTypes.string,
    device_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    folder_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    tau_sec: PropTypes.number,
  }),
  compact: PropTypes.bool,
};

CameraVideoPlayer.defaultProps = {
  selectedVideo: null,
  showDetections: true,
  setShowDetections: () => {},
  setExportModalVisible: () => {},
  isDetectionsLoading: false,
  customPlayerRef: null,
  seekToTime: null,
  seekTriggerId: null,
  canvasRef: null,
  onEnded: null,
  onProgress: undefined,
  variant: "excavator",
  crossVideo: null,
  compact: false,
};

export default CameraVideoPlayer;